import {
  TrackingElementContext,
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
  TrackingProductStreamElementContext,
  TrackingSectionContext,
} from '../../types';
import {getCategory, getSourceCategory} from './helpers/category';
import {
  TrackingClientState,
  TrackingClientStateHolder,
} from './helpers/clientState';
import {getSourceSortChannel} from './helpers/sortChannel';
import {
  getSourceHistory,
  getSourceHistoryEventData,
} from './helpers/sourceHistory';

export interface TrackingProductCustomData {
  variantId?: number | null;
  productId?: number | null;
  sponsoredType?: string | null;
  sortingChannel?: string | null;
  categoryName?: string | null;
  categoryId?: number | null;
  otherSource?: boolean;
  virtualCategoryId?: number | null;
  outfitId?: number | null;
  brandId?: number | null;
  storyId?: number | null;
  storyVirtualCategoryId?: number | null;
  storyEditorialId?: number | null;
}

export interface WebTrackingAdditionalData {
  sort: string;
  filter: string;
  fitAnalyticsUsed: string;
  source: string | null;
  sourceHistoryOne: string | null;
  sourceHistoryTwo: string | null;
  sourceHistoryThree: string | null;
  initiallyVisibleColumns: number;

  variantId?: number;
  sizeLabel?: string;
  supplierId?: number;

  category: string | null;
  categoryId: number | null;
  sourceCategory: string | null;
  sourceCategoryId: number | null;

  position: number;
  sectionIndexOnPage: number;
  rowIndexInSection: number;
  columnIndexInSection: number;

  strikeOutPrice?: string | null;
}

export interface TrackingProductAdditionalData
  extends TrackingProductCustomData {
  wt_sdk?: WebTrackingAdditionalData;
  [key: string]: unknown | never;
}

export const additionalDataForAddToBasket =
  (clientState: TrackingClientStateHolder) =>
  ({
    productId,
    variantId,
    productSponsoredType,
  }: {
    productId: number;
    variantId: number;
    productSponsoredType?: string;
  }): TrackingProductAdditionalData => {
    const currentClientState = clientState.getCurrent();

    const sourceSortChannel = getSourceSortChannel(currentClientState);

    return {
      ...getProductCustomData(clientState)(
        productId,
        productSponsoredType,
        variantId,
        sourceSortChannel
      ),
    };
  };

export const additionalDataForAddToWishlist =
  (clientState: TrackingClientStateHolder) =>
  ({
    section,
    element,
  }: {
    section: TrackingSectionContext;
    element: TrackingElementContext | TrackingProductStreamElementContext;
  }): TrackingProductAdditionalData => {
    const currentClientState = clientState.getCurrent();
    const category = getCategory({
      productCategories: null,
      state: currentClientState,
    });
    const sourceCategory = getSourceCategory(currentClientState);

    return {
      wt_sdk: {
        category: category.path,
        categoryId: category.id,
        sourceCategory: sourceCategory.path,
        sourceCategoryId: sourceCategory.id,

        position:
          element.type === 'element-context'
            ? element.elementIndexInSection + 1
            : element.productIndexOnPage + 1,
        sectionIndexOnPage: section.sectionIndexOnPage + 1,
        rowIndexInSection: element.rowIndexInSection + 1,
        columnIndexInSection: element.columnIndexInSection + 1,

        initiallyVisibleColumns:
          section.type === 'reco' ? section.initiallyVisibleColumns : 1,

        ...getSourceHistoryEventData({
          state: currentClientState,
          currentSourceSection: section,
        }),

        sort: currentClientState.sort,
        filter: '', // getFilterString(currentClientState),
        fitAnalyticsUsed: currentClientState.fitAnalyticsUsed,
      },
    };
  };

const getProductCustomData =
  (clientState: TrackingClientStateHolder) =>
  (
    productId: number,
    sponsoredType: string | undefined,
    variantId: number,
    sourceSortChannel?: string
  ): TrackingProductCustomData => {
    const currentState = clientState.getCurrent();
    const trackingData = getAdditionalTrackingData({
      trackingState: currentState,
    });

    return {
      variantId: variantId ?? null,
      productId: productId ?? null,
      sponsoredType: sponsoredType ?? null,
      sortingChannel: sourceSortChannel ?? null,
      ...trackingData,
    };
  };

const trackingDataOtherSource = {
  linkedContentType: null,
  linkedContentId: null,
};

const getAdditionalTrackingData = ({
  trackingState,
}: {
  trackingState: TrackingClientState;
}) => {
  const sourcePage = getSourcePage({trackingState});
  const previousPage = getSourceHistory({index: 1, state: trackingState});

  if (!sourcePage) {
    return trackingDataOtherSource;
  }

  switch (sourcePage.type) {
    case TrackingPageTypeWithId.Category:
      return {
        linkedContentType: 'category',
        linkedContentId: trackingState.lastVisitedCategory.id,
      };
    case TrackingPageTypeWithId.Story: {
      return {
        linkedContentType: 'story',
        linkedContentId: sourcePage.id,
      };
    }
    case TrackingPageTypeWithId.Brand: {
      return {
        linkedContentType: 'brand',
        linkedContentId: sourcePage.id,
      };
    }
    case TrackingPageTypeWithId.ODP: {
      return {
        linkedContentType: 'outfit',
        linkedContentId: sourcePage.id,
      };
    }
    case TrackingPageTypeWithoutId.Wishlist: {
      return {
        linkedContentType: 'wishlist',
        linkedContentId: null,
      };
    }
    case TrackingPageTypeWithoutId.Search: {
      return {
        linkedContentType: 'search',
        linkedContentId: null,
      };
    }
    case TrackingPageTypeWithId.ADP: {
      const comesFromWishlist = [
        previousPage,
        getSourceHistory({index: 2, state: trackingState}),
      ].some(
        history => history?.page.type === TrackingPageTypeWithoutId.Wishlist
      );

      if (!sourcePage) {
        return trackingDataOtherSource;
      }

      if (comesFromWishlist) {
        return {
          linkedContentType: 'wishlist',
          linkedContentId: null,
        };
      }

      if (previousPage?.page.type === TrackingPageTypeWithId.ADP) {
        return {
          linkedContentType: 'product',
          linkedContentId: trackingState.previousProductId,
        };
      }

      return trackingDataOtherSource;
    }
    default:
      return trackingDataOtherSource;
  }
};

const getSourcePage = ({
  trackingState,
}: {
  trackingState: TrackingClientState;
}): TrackingPageContext | null => {
  const currentPage = getSourceHistory({index: 0, state: trackingState});
  const sourcePage = getSourceHistory({index: 1, state: trackingState});

  if (!currentPage || !sourcePage) {
    return null;
  }

  const isOnADP =
    currentPage.page.type === TrackingPageTypeWithId.ADP ||
    currentPage.page.type === TrackingPageTypeWithId.AdpQuickView;

  if (isOnADP) {
    return sourcePage.page;
  }

  if (
    [
      TrackingPageTypeWithId.Category,
      TrackingPageTypeWithId.Story,
      TrackingPageTypeWithoutId.Wishlist,
      TrackingPageTypeWithoutId.Search,
    ].includes(currentPage.page.type)
  ) {
    return currentPage.page;
  }

  return null;
};
